import { useState, forwardRef, useImperativeHandle } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import type { OrgaAdminGruppeDTO } from "@/lib/orgaAdmin/orgaAdmin.types";
import type { StudentKlausur } from "@/lib/orgaAdmin/orgaAdminKlausur.service";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export interface StudentKlausurWithKlausurInfo extends StudentKlausur {
	klausurTitle: string;
	klausurIndex: number;
	gruppeTitle: string;
}

interface StudentKlausurDialogsProps {
	// Actions
	onDeleteStudentKlausur: (studentKlausurId: string) => Promise<void>;
	onChangeAusschuss: (
		studentKlausurId: string,
		newGruppeId: string,
	) => Promise<void>;

	// Data
	groups: OrgaAdminGruppeDTO[];
	isLoadingGroups: boolean;
}

export interface StudentKlausurDialogsRef {
	openDeleteDialog: (studentKlausur: StudentKlausurWithKlausurInfo) => void;
	openChangeAusschussDialog: (
		studentKlausur: StudentKlausurWithKlausurInfo,
	) => void;
}

/**
 * Component containing the Delete and Change Ausschuss dialogs for Student Klausuren
 * Uses imperative handle to expose dialog open methods to parent
 */
const StudentKlausurDialogs = forwardRef<
	StudentKlausurDialogsRef,
	StudentKlausurDialogsProps
>(
	(
		{ onDeleteStudentKlausur, onChangeAusschuss, groups, isLoadingGroups },
		ref,
	) => {
		// Delete Dialog State
		const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
		const [studentKlausurToDelete, setStudentKlausurToDelete] =
			useState<StudentKlausurWithKlausurInfo | null>(null);
		const [isDeleting, setIsDeleting] = useState(false);

		// Change Ausschuss Dialog State
		const [isChangeAusschussDialogOpen, setIsChangeAusschussDialogOpen] =
			useState(false);
		const [
			studentKlausurToChangeAusschuss,
			setStudentKlausurToChangeAusschuss,
		] = useState<StudentKlausurWithKlausurInfo | null>(null);
		const [newGruppeId, setNewGruppeId] = useState<string>("");
		const [isChangingAusschuss, setIsChangingAusschuss] = useState(false);

		// Expose methods to parent via ref
		useImperativeHandle(ref, () => ({
			openDeleteDialog: (studentKlausur: StudentKlausurWithKlausurInfo) => {
				setStudentKlausurToDelete(studentKlausur);
				setIsDeleteDialogOpen(true);
			},
			openChangeAusschussDialog: (
				studentKlausur: StudentKlausurWithKlausurInfo,
			) => {
				setStudentKlausurToChangeAusschuss(studentKlausur);
				setNewGruppeId(studentKlausur.gruppeId);
				setIsChangeAusschussDialogOpen(true);
			},
		}));

		const hasAssignedKorrektors = (
			studentKlausur: StudentKlausurWithKlausurInfo,
		): boolean => {
			if (!studentKlausur.korrekturSteps) return false;
			return studentKlausur.korrekturSteps.some(
				(step) => step.korrektorId !== null && step.korrektorId !== undefined,
			);
		};

		const getAssignedKorrektors = (
			studentKlausur: StudentKlausurWithKlausurInfo,
		): string[] => {
			if (!studentKlausur.korrekturSteps) return [];
			return studentKlausur.korrekturSteps
				.filter((step) => step.korrektor)
				.map(
					(step) =>
						step.korrektor?.name || step.korrektor?.email || "Unbekannt",
				);
		};

		const hasExistingKorrekturen = (
			studentKlausur: StudentKlausurWithKlausurInfo,
		): boolean => {
			if (!studentKlausur.korrekturSteps) return false;
			return studentKlausur.korrekturSteps.some(
				(step) =>
					step.korrekturStudentKlausuren &&
					step.korrekturStudentKlausuren.length > 0,
			);
		};

		const handleDeleteStudentKlausur = async () => {
			if (!studentKlausurToDelete) return;

			setIsDeleting(true);
			try {
				await onDeleteStudentKlausur(studentKlausurToDelete.id);
				setIsDeleteDialogOpen(false);
				setStudentKlausurToDelete(null);
			} catch (error) {
				console.error("Error deleting student klausur:", error);
				if (error instanceof Error) {
					toast.error(
						error.message || "Fehler beim Löschen der Studentenklausur",
					);
				} else {
					toast.error("Fehler beim Löschen der Studentenklausur");
				}
			} finally {
				setIsDeleting(false);
			}
		};

		const handleChangeAusschuss = async () => {
			if (!studentKlausurToChangeAusschuss || !newGruppeId) return;

			setIsChangingAusschuss(true);
			try {
				await onChangeAusschuss(
					studentKlausurToChangeAusschuss.id,
					newGruppeId,
				);
				setIsChangeAusschussDialogOpen(false);
				setStudentKlausurToChangeAusschuss(null);
				setNewGruppeId("");
			} catch (error) {
				console.error("Error changing ausschuss:", error);
				if (error instanceof Error) {
					toast.error(error.message || "Fehler beim Ändern des Ausschusses");
				} else {
					toast.error("Fehler beim Ändern des Ausschusses");
				}
			} finally {
				setIsChangingAusschuss(false);
			}
		};
		return (
			<>
				{/* Delete Student Klausur Confirmation Dialog */}
				<Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
					<DialogContent className="bg-card">
						<DialogHeader>
							<DialogTitle>Studentenklausur löschen</DialogTitle>
							<DialogDescription>
								{studentKlausurToDelete &&
								hasExistingKorrekturen(studentKlausurToDelete)
									? "Diese Studentenklausur kann nicht gelöscht werden."
									: "Sind Sie sicher, dass Sie diese Studentenklausur löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden."}
							</DialogDescription>
						</DialogHeader>
						{studentKlausurToDelete && (
							<div className="py-4 space-y-2">
								<div className="bg-muted p-3 rounded-lg">
									<div className="font-semibold">
										{studentKlausurToDelete.internalIdentifier}
									</div>
									<div className="text-sm text-muted-foreground">
										Klausur: {studentKlausurToDelete.klausurTitle}
									</div>
									<div className="text-sm text-muted-foreground">
										Ausschuss: {studentKlausurToDelete.gruppeTitle}
									</div>
								</div>

								{hasExistingKorrekturen(studentKlausurToDelete) && (
									<div className="bg-destructive/10 border border-destructive/20 p-3 rounded-lg">
										<div className="flex items-start gap-2">
											<AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
											<div className="flex-1">
												<p className="font-semibold text-destructive mb-1">
													Löschen nicht möglich
												</p>
												<p className="text-sm text-destructive/90">
													Diese Studentenklausur hat bereits Korrekturen und
													kann nicht gelöscht werden. Korrekturen müssen zuerst
													entfernt werden.
												</p>
											</div>
										</div>
									</div>
								)}
							</div>
						)}
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => {
									setIsDeleteDialogOpen(false);
									setStudentKlausurToDelete(null);
								}}
								disabled={isDeleting}
							>
								{studentKlausurToDelete &&
								hasExistingKorrekturen(studentKlausurToDelete)
									? "Schließen"
									: "Abbrechen"}
							</Button>
							{studentKlausurToDelete &&
								!hasExistingKorrekturen(studentKlausurToDelete) && (
									<Button
										variant="destructive"
										onClick={handleDeleteStudentKlausur}
										disabled={isDeleting}
									>
										{isDeleting ? "Wird gelöscht..." : "Löschen"}
									</Button>
								)}
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Change Ausschuss Dialog */}
				<Dialog
					open={isChangeAusschussDialogOpen}
					onOpenChange={setIsChangeAusschussDialogOpen}
				>
					<DialogContent className="bg-card">
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2">
								<AlertTriangle className="h-5 w-5 text-amber-500" />
								Ausschuss ändern
							</DialogTitle>
							<DialogDescription>
								Achtung: Das Ändern des Ausschusses kann Auswirkungen auf
								bestehende Korrekturen haben. Stellen Sie sicher, dass diese
								Änderung korrekt ist.
							</DialogDescription>
						</DialogHeader>
						{studentKlausurToChangeAusschuss && (
							<div className="py-4 space-y-4">
								<div className="bg-muted p-3 rounded-lg space-y-1">
									<div className="font-semibold">
										{studentKlausurToChangeAusschuss.internalIdentifier}
									</div>
									<div className="text-sm text-muted-foreground">
										Klausur: {studentKlausurToChangeAusschuss.klausurTitle}
									</div>
									<div className="text-sm text-muted-foreground">
										Aktueller Ausschuss:{" "}
										{studentKlausurToChangeAusschuss.gruppeTitle}
									</div>
								</div>

								{hasAssignedKorrektors(studentKlausurToChangeAusschuss) && (
									<div className="bg-destructive/10 border border-destructive/20 p-3 rounded-lg">
										<div className="flex items-start gap-2">
											<AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
											<div className="flex-1">
												<p className="font-semibold text-destructive mb-1">
													Ausschuss kann nicht geändert werden
												</p>
												<p className="text-sm text-destructive/90">
													Dieser Studentenklausur sind bereits Korrektor*innen
													zugewiesen. Bitte entfernen Sie zuerst alle
													Zuweisungen, bevor Sie den Ausschuss ändern.
												</p>
												<div className="mt-2">
													<p className="text-sm font-medium text-destructive">
														Zugewiesene Korrektor*innen:
													</p>
													<ul className="list-disc list-inside text-sm text-destructive/90 mt-1">
														{getAssignedKorrektors(
															studentKlausurToChangeAusschuss,
														).map((name, idx) => (
															<li key={idx}>{name}</li>
														))}
													</ul>
												</div>
											</div>
										</div>
									</div>
								)}

								{!hasAssignedKorrektors(studentKlausurToChangeAusschuss) && (
									<div className="space-y-2">
										<label className="text-sm font-medium">
											Neuer Ausschuss / Gruppe
										</label>
										<Select
											value={newGruppeId}
											onValueChange={setNewGruppeId}
											disabled={isLoadingGroups}
										>
											<SelectTrigger>
												<SelectValue
													placeholder={
														isLoadingGroups
															? "Gruppen werden geladen..."
															: "Neue Gruppe auswählen..."
													}
												/>
											</SelectTrigger>
											<SelectContent>
												{groups.map((group) => (
													<SelectItem key={group.id} value={group.id}>
														{group.title}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								)}
							</div>
						)}
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => {
									setIsChangeAusschussDialogOpen(false);
									setStudentKlausurToChangeAusschuss(null);
									setNewGruppeId("");
								}}
								disabled={isChangingAusschuss}
							>
								{studentKlausurToChangeAusschuss &&
								hasAssignedKorrektors(studentKlausurToChangeAusschuss)
									? "Schließen"
									: "Abbrechen"}
							</Button>
							{studentKlausurToChangeAusschuss &&
								!hasAssignedKorrektors(studentKlausurToChangeAusschuss) && (
									<Button
										onClick={handleChangeAusschuss}
										disabled={
											isChangingAusschuss ||
											!newGruppeId ||
											newGruppeId === studentKlausurToChangeAusschuss?.gruppeId
										}
									>
										{isChangingAusschuss
											? "Wird geändert..."
											: "Ausschuss ändern"}
									</Button>
								)}
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</>
		);
	},
);

StudentKlausurDialogs.displayName = "StudentKlausurDialogs";

export default StudentKlausurDialogs;
